import { Table as BaseTable, TableProps } from '@/components/table'
import { Query } from './table_query'
import { AccommodationsQuery } from './__generated__/table_query'
import { useQuery } from '@apollo/client'
import { Inline as Error } from '@/components/error'
import { Inline as Loading } from '@/components/loading'
import { SortOrder, AccommodationFilters, VenueOrder } from '@/types'
import { Button } from '../base/button'
import { dedupe } from '@/helpers/dedupe'
import { useTable } from '@/helpers/useTable'
import { ShowDialog } from './show_dialog'
import { NewDialog } from './new_dialog'

interface Props extends TableProps {
  createable?: boolean
}

export const Table = ({ pageSize = 10, onIndex = false, createable = false, ...props }: Props) => {
  const {
    data,
    loading,
    error,
    refetch,
    fetchMore
  } = useQuery<AccommodationsQuery>(Query, { variables: { first: pageSize } })
  const {
    pageInfo,
    openNew,
    setOpenNew,
    openShow,
    showShow,
    hideShow,
    resourceId
  } = useTable({ pageInfo: data?.accommodations?.pageInfo, onIndex, basePath: '/accommodation' })

  if (error) { return <Error message={error.message} /> }
  if (loading || !data) { return <Loading /> }

  return (
    <>
      <BaseTable<AccommodationFilters, VenueOrder>
        {...props}
        actionButton={createable ? <Button color="green" onClick={() => setOpenNew(true)}>Add</Button> : undefined}
        defaultSort={{ name: SortOrder.Asc }}
        moreToFetch={pageInfo.current?.hasNextPage}
        refetch={refetch}
        fetchMore={() => fetchMore({
          variables: { after: pageInfo.current?.endCursor },
          updateQuery(previousData, { fetchMoreResult }) {
            if (Object.keys(previousData).length === 0) return fetchMoreResult
            fetchMoreResult.accommodations.nodes = dedupe([
              ...previousData.accommodations.nodes,
              ...fetchMoreResult.accommodations.nodes
            ])
            return { ...fetchMoreResult }
          }
        })}
        filters={[
          {
            index: 0,
            setValue: (set, value) => set(f => ({ ...f, nameCont: value })),
            type: 'text',
            placeholder: 'Now, what did we call it?'
          },
        ]}
        headings={[
          { name: 'Name', sortField: 'name', defaultDirection: SortOrder.Asc },
          { name: 'Address' },
        ]}
        rows={data.accommodations.nodes.map(accommodation => (
          {
            id: accommodation.id,
            columns: [
              accommodation.name,
              accommodation.address,
            ]
          }
        ))}
        onRowClick={showShow}
      />
      { resourceId && <ShowDialog id={resourceId} open={openShow} setOpen={hideShow} /> }
      <NewDialog open={openNew} setOpen={setOpenNew} />
    </>
  )
}
